'use client'

import Link from 'next/link'
import { LoaderCircle, MapPin } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { RefObject } from 'react'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/combobox'
import { cn } from '@/lib/utils'
import { cityOptionFromLabel, mergeCityOptions, type CityOption } from './location-options'

export function LocationCombobox({
  name = 'location',
  value,
  suggestions,
  className,
  variant = 'hero',
  portalContainer,
  latitude,
  longitude,
}: {
  name?: string
  value: string
  suggestions: string[]
  className?: string
  variant?: 'hero' | 'toolbar' | 'mobile'
  portalContainer?: RefObject<HTMLElement | null>
  latitude?: number
  longitude?: number
}) {
  const initialOption = useMemo(() => ({ ...cityOptionFromLabel(value), latitude, longitude }), [latitude, longitude, value])
  const preferred = useMemo(
    () => mergeCityOptions(
      [initialOption, ...suggestions.filter((label) => label !== value).map(cityOptionFromLabel)],
      [],
    ),
    [initialOption, suggestions, value],
  )
  const [selected, setSelected] = useState<CityOption>(initialOption)
  const [inputValue, setInputValue] = useState('')
  const [remoteState, setRemoteState] = useState<{ query: string; results: CityOption[]; loading: boolean }>({ query: '', results: [], loading: false })
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const query = inputValue.trim()
    if (!open || query.length < 2 || query === selected.label) return

    const controller = new AbortController()
    const timeout = window.setTimeout(async () => {
      setRemoteState({ query, results: [], loading: true })
      try {
        const response = await fetch(`/api/locations?q=${encodeURIComponent(query)}`, {
          cache: 'force-cache',
          signal: controller.signal,
        })
        const payload = await response.json() as { results?: CityOption[] }
        if (!response.ok) throw new Error('City search unavailable')
        if (!controller.signal.aborted) setRemoteState({ query, results: Array.isArray(payload.results) ? payload.results : [], loading: false })
      } catch {
        if (!controller.signal.aborted) setRemoteState({ query, results: [], loading: false })
      }
    }, 350)

    return () => {
      window.clearTimeout(timeout)
      controller.abort()
    }
  }, [inputValue, open, selected.label])

  const matchingPreferred = inputValue.trim().length < 2
    ? preferred
    : preferred.filter((option) => option.label.toLowerCase().includes(inputValue.trim().toLowerCase()))
  const activeRemote = remoteState.query === inputValue.trim() ? remoteState.results : []
  const searching = remoteState.query === inputValue.trim() && remoteState.loading
  const options = mergeCityOptions(matchingPreferred, activeRemote)

  return (
    <div className={cn('location-combobox', `location-combobox--${variant}`, className)}>
      <Combobox
        items={options}
        value={selected}
        inputValue={inputValue}
        onInputValueChange={setInputValue}
        onOpenChange={setOpen}
        onValueChange={(option) => {
          if (option) setSelected(option)
        }}
        itemToStringLabel={(option) => option.label}
        itemToStringValue={(option) => option.label}
        isItemEqualToValue={(option, current) => option.id === current.id}
        filter={null}
        autoHighlight
      >
        <ComboboxTrigger ref={triggerRef} className="location-combobox-trigger" aria-label={`Search city. Current location: ${selected.label}`}>
          <MapPin />
          <span><small>Where</small><strong>{selected.label}</strong></span>
        </ComboboxTrigger>
        <ComboboxContent anchor={triggerRef} className="location-combobox-content" align="start" sideOffset={8} portalContainer={portalContainer}>
          <ComboboxInput placeholder="Search cities" aria-label="Search cities" showClear />
          <ComboboxEmpty>{searching ? 'Searching cities…' : inputValue.trim().length < 2 ? 'Type at least 2 characters.' : 'No cities found.'}</ComboboxEmpty>
          <ComboboxList>
            {(option: CityOption) => (
              <ComboboxItem key={option.id} value={option}>
                <MapPin />
                <span className="location-option-copy"><strong>{option.city}</strong><small>{[option.region, option.country && option.country !== 'United States' ? option.country : undefined].filter(Boolean).join(', ')}</small></span>
                {option.source === 'tables' ? <em>On Trellis Tables</em> : null}
              </ComboboxItem>
            )}
          </ComboboxList>
          <div className="location-combobox-footer">
            {searching ? <LoaderCircle aria-hidden="true" /> : null}
            <span>City data © <Link href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</Link></span>
          </div>
        </ComboboxContent>
      </Combobox>
      <input type="hidden" name={name} value={selected.label} />
      {selected.latitude !== undefined ? <input type="hidden" name="latitude" value={selected.latitude} /> : null}
      {selected.longitude !== undefined ? <input type="hidden" name="longitude" value={selected.longitude} /> : null}
    </div>
  )
}
